/**
 * How long a pending approval has left.
 *
 * WHY THIS EXISTS
 * ───────────────
 * The source pane can be collapsed while an approval is waiting, which is the
 * point — you park the review and keep working. But the gate behind it holds an
 * open SSE stream and stops accepting the approval after a fixed window
 * (_APPROVAL_TIMEOUT_SECONDS in chat.py). Without a visible clock, "minimize"
 * silently promises persistence the backend does not have, and the user finds
 * out only when they come back and press Approve on a dead card.
 *
 * So the affordance and the reality are kept in sync by showing the deadline
 * rather than by pretending there isn't one.
 *
 * The deadline is server-sent (`expires_at` on the tool_approval_required
 * event). It is deliberately NOT recomputed client-side from a constant — a
 * duplicated timeout is a value that drifts from the one that actually governs.
 */

import { useEffect, useState } from 'react'

/** Seconds remaining until `expiresAt`, or null when there is no deadline. */
export function useSecondsRemaining(expiresAt) {
  const compute = () => {
    if (!expiresAt) return null
    const ms = new Date(expiresAt).getTime() - Date.now()
    if (Number.isNaN(ms)) return null
    return Math.max(0, Math.round(ms / 1000))
  }

  const [remaining, setRemaining] = useState(compute)

  useEffect(() => {
    if (!expiresAt) {
      setRemaining(null)
      return
    }
    setRemaining(compute())
    const id = setInterval(() => setRemaining(compute()), 1000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt])

  return remaining
}

export function formatRemaining(seconds) {
  if (seconds == null) return ''
  if (seconds <= 0) return 'expired'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`
}

/**
 * Inline countdown. Goes amber under two minutes and red once expired, so the
 * state is legible from the collapsed rail without reading the number.
 */
export default function ApprovalCountdown({ expiresAt, className = '' }) {
  const remaining = useSecondsRemaining(expiresAt)
  if (remaining == null) return null

  const expired = remaining <= 0
  const urgent = !expired && remaining <= 120

  const tone = expired
    ? 'text-red-700 dark:text-red-400'
    : urgent
      ? 'text-amber-700 dark:text-amber-400'
      : 'text-gray-500 dark:text-gray-400'

  return (
    <span className={`${tone} ${className}`} role={expired || urgent ? 'status' : undefined}>
      {expired ? 'Approval window closed' : `Expires in ${formatRemaining(remaining)}`}
    </span>
  )
}
